import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Trash, MagnifyingGlass, TrendUp, TrendDown, UploadSimple } from "@phosphor-icons/react";
import { CATEGORIES, newItem } from "@/lib/calc";
import { formatINR, formatPct } from "@/lib/format";

function NumCell({ value, onCommit, className = "", placeholder, testId, allowEmpty = false }) {
  const [text, setText] = useState(value === null || value === undefined ? "" : String(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setText(value === null || value === undefined ? "" : String(value));
  }, [value, focused]);
  return (
    <input
      type="number" inputMode="decimal" step="any" data-testid={testId} placeholder={placeholder}
      className={`boq-input text-right ${className}`} value={text}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
      onChange={(e) => {
        setText(e.target.value);
        if (e.target.value === "") onCommit(allowEmpty ? null : 0);
        else if (!Number.isNaN(Number(e.target.value))) onCommit(Number(e.target.value));
      }}
    />
  );
}

const Row = memo(function Row({ item, c, idx, defaultDed, onChange, onDelete }) {
  const set = (k) => (v) => onChange(item.item_id, { [k]: v });
  const profit = c.profit >= 0;
  const td = "px-1.5 py-1.5 border-b border-slate-100 align-top";
  const num = "px-3 py-2.5 border-b border-slate-100 text-right tabular whitespace-nowrap align-top";
  return (
    <tr className="hover:bg-slate-50/70 transition-colors" data-testid={`boq-row-${idx}`}>
      <td className="px-3 py-2.5 border-b border-slate-100 text-xs text-slate-400 tabular align-top">{idx + 1}</td>
      <td className={`${td} min-w-[80px]`}>
        <input className="boq-input" value={item.item_no} data-testid={`boq-item-no-${idx}`} onChange={(e) => set("item_no")(e.target.value)} />
      </td>
      <td className={`${td} min-w-[280px]`}>
        <textarea rows={2} className="boq-input resize-y leading-snug" value={item.description} data-testid={`boq-description-${idx}`} onChange={(e) => set("description")(e.target.value)} />
      </td>
      <td className={`${td} min-w-[180px]`}>
        <select className="boq-input cursor-pointer" value={item.category} data-testid={`boq-category-${idx}`} onChange={(e) => set("category")(e.target.value)}>
          {CATEGORIES.map((cat) => <option key={cat}>{cat}</option>)}
        </select>
      </td>
      <td className={`${td} min-w-[110px]`}><NumCell value={item.quantity} onCommit={set("quantity")} testId={`boq-qty-${idx}`} /></td>
      <td className={`${td} min-w-[76px]`}><input className="boq-input" value={item.unit} data-testid={`boq-unit-${idx}`} onChange={(e) => set("unit")(e.target.value)} /></td>
      <td className={`${td} min-w-[120px]`}><NumCell value={item.govt_rate} onCommit={set("govt_rate")} testId={`boq-govt-rate-${idx}`} /></td>
      <td className={`${td} min-w-[76px]`}><NumCell value={item.deduction} onCommit={set("deduction")} allowEmpty placeholder={String(defaultDed)} testId={`boq-deduction-${idx}`} /></td>
      <td className={num}>{formatINR(c.effective)}</td>
      <td className={`${td} min-w-[130px]`}><NumCell value={item.market_rate || ""} onCommit={set("market_rate")} className="market" placeholder="Enter cost" testId={`boq-market-rate-${idx}`} /></td>
      <td className={num} data-testid={`boq-revenue-${idx}`}>{formatINR(c.revenue)}</td>
      <td className={num}>{formatINR(c.expenditure)}</td>
      <td className={`${num} font-semibold ${profit ? "text-emerald-600" : "text-rose-600"}`} data-testid={`boq-profit-${idx}`}>
        <span className="inline-flex items-center gap-1">{profit ? <TrendUp size={13} weight="bold" /> : <TrendDown size={13} weight="bold" />}{formatINR(c.profit)}</span>
      </td>
      <td className={`${num} ${profit ? "text-emerald-600" : "text-rose-600"}`}>{formatPct(c.margin)}</td>
      <td className="px-2 py-2 border-b border-slate-100 align-top">
        <button data-testid={`boq-delete-${idx}`} onClick={() => onDelete(item.item_id)} className="p-1.5 rounded-sm text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors" title="Delete item">
          <Trash size={16} />
        </button>
      </td>
    </tr>
  );
});

const HEADERS = [
  ["#", ""], ["Item No", ""], ["Description", ""], ["Category", ""], ["Qty", "r"], ["Unit", ""], ["Govt Rate", "r"], ["Below %", "r"],
  ["Effective Rate", "r"], ["My Market Cost / Unit", "r"], ["Revenue", "r"], ["Expenditure", "r"], ["Profit / Loss", "r"], ["Margin", "r"], ["", ""],
];

export function BoqTable({ project, calc, update, onGoto }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [unpriced, setUnpriced] = useState(false);

  const onChange = useCallback((id, patch) => update((p) => ({ items: p.items.map((i) => (i.item_id === id ? { ...i, ...patch } : i)) })), [update]);
  const onDelete = useCallback((id) => update((p) => ({ items: p.items.filter((i) => i.item_id !== id) })), [update]);
  const add = () => { update((p) => ({ items: [...p.items, newItem()] })); setQ(""); setCat("all"); setUnpriced(false); };
  const clearAll = () => window.confirm(`Remove all ${project.items.length} BOQ items from this project? This cannot be undone.`) && update({ items: [] });

  const visible = useMemo(() => project.items.map((it, idx) => ({ it, idx })).filter(({ it }) =>
    (cat === "all" || it.category === cat) && (!unpriced || !(Number(it.market_rate) > 0)) &&
    `${it.item_no} ${it.description}`.toLowerCase().includes(q.toLowerCase())), [project.items, q, cat, unpriced]);

  if (!project.items.length) {
    return (
      <div data-testid="boq-empty" className="border-2 border-dashed border-slate-300 rounded-sm bg-white p-12 text-center">
        <UploadSimple className="mx-auto text-slate-400" size={44} />
        <h3 className="font-heading text-xl font-bold mt-4">No BOQ items</h3>
        <p className="text-sm text-slate-500 mt-1">Upload a Government BOQ or add items manually.</p>
        <div className="mt-5 flex justify-center gap-3">
          <button data-testid="boq-empty-upload" onClick={() => onGoto("upload")} className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-sm hover:bg-emerald-700 transition-colors">Upload BOQ</button>
          <button data-testid="boq-empty-add" onClick={add} className="px-4 py-2 bg-white border border-slate-300 text-slate-700 text-sm font-medium rounded-sm hover:bg-slate-50 transition-colors">Add item manually</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-3 md:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <div className="relative sm:w-72">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input data-testid="boq-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search item no. or description"
              className="w-full h-10 pl-9 pr-3 text-sm bg-white border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500" />
          </div>
          <select data-testid="boq-category-filter" value={cat} onChange={(e) => setCat(e.target.value)}
            className="h-10 px-3 text-sm bg-white border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30">
            <option value="all">All categories</option>
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
            <input type="checkbox" data-testid="boq-unpriced-toggle" checked={unpriced} onChange={(e) => setUnpriced(e.target.checked)} className="accent-emerald-600 h-4 w-4" />
            Needs market cost
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 h-10 pl-3 pr-1 bg-amber-50 border border-amber-200 rounded-sm text-sm font-medium text-amber-800">
            Tender % below
            <input type="number" step="0.01" min="0" max="100" data-testid="boq-below-percent-input" value={project.default_deduction ?? ""}
              onChange={(e) => update({ default_deduction: e.target.value === "" ? 0 : Math.min(100, Math.max(0, Number(e.target.value))) })}
              className="w-20 h-8 px-2 text-right tabular bg-white border border-amber-300 rounded-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500" />
          </label>
          <button data-testid="boq-clear-all-button" onClick={clearAll} className="inline-flex items-center gap-2 h-10 px-4 bg-white border border-rose-200 text-rose-600 text-sm font-medium rounded-sm hover:bg-rose-50 transition-colors">
            <Trash size={16} /> Clear all
          </button>
          <button data-testid="boq-add-item-button" onClick={add} className="inline-flex items-center gap-2 h-10 px-4 bg-white border border-slate-300 text-slate-700 text-sm font-medium rounded-sm hover:bg-slate-50 transition-colors shadow-sm">
            <Plus size={16} weight="bold" /> Add Item
          </button>
        </div>
      </div>

      <div className="w-full overflow-auto max-h-[70vh] rounded-sm border border-slate-200 bg-white shadow-sm" data-testid="boq-table">
        <table className="w-full text-left border-collapse text-sm">
          <thead>
            <tr>
              {HEADERS.map(([h, a], i) => (
                <th key={i} className={`bg-slate-50 text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-3 py-3 border-b border-slate-200 sticky top-0 z-10 whitespace-nowrap ${a === "r" ? "text-right" : ""} ${h.startsWith("My Market") ? "text-amber-700 bg-amber-50" : ""}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map(({ it, idx }) => (
              <Row key={it.item_id} item={it} c={calc.rows[idx]} idx={idx} defaultDed={project.default_deduction} onChange={onChange} onDelete={onDelete} />
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-slate-900 text-white font-semibold text-sm">
              <td colSpan={10} className="px-3 py-3 sticky bottom-0 bg-slate-900">Project Total · {calc.itemCount} items</td>
              <td className="px-3 py-3 text-right tabular whitespace-nowrap sticky bottom-0 bg-slate-900" data-testid="boq-total-revenue">{formatINR(calc.revenue)}</td>
              <td className="px-3 py-3 text-right tabular whitespace-nowrap sticky bottom-0 bg-slate-900" data-testid="boq-total-expenditure">{formatINR(calc.expenditure)}</td>
              <td className={`px-3 py-3 text-right tabular whitespace-nowrap sticky bottom-0 bg-slate-900 ${calc.profit >= 0 ? "text-emerald-300" : "text-rose-300"}`} data-testid="boq-total-profit">{formatINR(calc.profit)}</td>
              <td className="px-3 py-3 text-right tabular sticky bottom-0 bg-slate-900">{formatPct(calc.margin)}</td>
              <td className="sticky bottom-0 bg-slate-900" />
            </tr>
          </tfoot>
        </table>
      </div>
      {visible.length === 0 && <p className="text-sm text-slate-500" data-testid="boq-no-match">No items match the current filters.</p>}
    </div>
  );
}
